<script setup lang="ts" generic="T extends string | number">
import { computed, nextTick, ref, useId, watch } from 'vue'
import { filterOptions } from '../comboBox'

// A select you can type into (combobox, list autocomplete): for long lists, e.g. the hundreds of models of a provider.
// Typing filters the options (every word, any order, ignoring case and accents); the value is always one of the
// options. Arrows move, Enter picks, Esc closes and puts the chosen label back. Same look as UiSelect.
const props = withDefaults(
  defineProps<{
    label: string
    options: ReadonlyArray<{ value: T; label: string }>
    hint?: string
    error?: string
    placeholder?: string
    // Shown when nothing matches what was typed.
    emptyText: string
    // Shown under the list when more options match than are drawn ({count} = how many were left out).
    moreText?: (count: number) => string
    compact?: boolean
    disabled?: boolean
    id?: string
    // How many matching options are drawn at once (the rest after typing more).
    maxResults?: number
  }>(),
  { maxResults: 200 },
)

const model = defineModel<T>({ required: true })

const generatedId = useId()
const inputId = computed(() => props.id ?? `combo-${generatedId}`)
const listId = computed(() => `${inputId.value}-list`)
const hintId = computed(() => (props.hint ? `${inputId.value}-hint` : undefined))

const open = ref(false)
const typed = ref(false)
const query = ref('')
const active = ref(-1)
const inputRef = ref<HTMLInputElement | null>(null)
const listRef = ref<HTMLElement | null>(null)

const selectedLabel = computed(() => props.options.find((option) => option.value === model.value)?.label ?? '')

// Until the person types, the whole list (with the chosen one in view); after, only what matches.
const matches = computed(() => (typed.value ? filterOptions(props.options, query.value) : props.options))
const shown = computed(() => matches.value.slice(0, props.maxResults))
const hidden = computed(() => matches.value.length - shown.value.length)

watch(selectedLabel, (label) => {
  if (!open.value) query.value = label
}, { immediate: true })

function show() {
  if (props.disabled || open.value) return
  open.value = true
  typed.value = false
  query.value = selectedLabel.value
  active.value = Math.max(0, shown.value.findIndex((option) => option.value === model.value))
  void nextTick(() => {
    inputRef.value?.select()
    scrollToActive()
  })
}

function close() {
  open.value = false
  typed.value = false
  query.value = selectedLabel.value
}

function pick(option: { value: T; label: string }) {
  model.value = option.value
  open.value = false
  typed.value = false
  query.value = option.label
}

function onInput(event: Event) {
  query.value = (event.target as HTMLInputElement).value
  typed.value = true
  open.value = true
  active.value = shown.value.length ? 0 : -1
  void nextTick(scrollToActive)
}

function move(step: number) {
  if (!open.value) {
    show()
    return
  }
  const count = shown.value.length
  if (!count) return
  active.value = (active.value + step + count) % count
  void nextTick(scrollToActive)
}

function onKey(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') move(1)
  else if (event.key === 'ArrowUp') move(-1)
  else if (event.key === 'Enter' && open.value) {
    const option = shown.value[active.value]
    if (option) pick(option)
  } else if (event.key === 'Escape' && open.value) close()
  else return
  event.preventDefault()
  // Esc inside a dialog closes only the list.
  event.stopPropagation()
}

function scrollToActive() {
  // Optional call: jsdom (tests) has no scrollIntoView.
  listRef.value?.querySelector<HTMLElement>(`[data-index="${active.value}"]`)?.scrollIntoView?.({ block: 'nearest' })
}

const activeId = computed(() => (open.value && active.value >= 0 && shown.value[active.value] ? `${listId.value}-${active.value}` : undefined))
</script>

<template>
  <div class="ui-combo">
    <label :for="inputId" class="ui-combo__label" :class="{ 'ui-combo__label--muted': compact }">{{ label }}</label>
    <div class="ui-combo__field">
      <input
        :id="inputId"
        ref="inputRef"
        :value="query"
        type="text"
        role="combobox"
        autocomplete="off"
        spellcheck="false"
        aria-autocomplete="list"
        :aria-expanded="open"
        :aria-controls="listId"
        :aria-activedescendant="activeId"
        :aria-invalid="!!error || undefined"
        :aria-describedby="hintId"
        :placeholder="placeholder"
        :disabled="disabled"
        class="ui-combo__control"
        :class="{ 'ui-combo__control--compact': compact, 'ui-combo__control--invalid': !!error }"
        @focus="show"
        @click="show"
        @input="onInput"
        @keydown="onKey"
        @blur="close"
      />
      <ul
        v-show="open"
        :id="listId"
        ref="listRef"
        role="listbox"
        :aria-label="label"
        class="ui-combo__list"
        @mousedown.prevent
      >
        <li
          v-for="(option, index) in shown"
          :id="`${listId}-${index}`"
          :key="String(option.value)"
          :data-index="index"
          role="option"
          :aria-selected="option.value === model"
          class="ui-combo__option"
          :class="{ 'ui-combo__option--active': index === active, 'ui-combo__option--selected': option.value === model }"
          @click="pick(option)"
          @mousemove="active = index"
        >
          {{ option.label }}
        </li>
        <li v-if="!shown.length" class="ui-combo__note" role="presentation">{{ emptyText }}</li>
        <li v-else-if="hidden > 0 && moreText" class="ui-combo__note" role="presentation">{{ moreText(hidden) }}</li>
      </ul>
    </div>
    <span v-if="error" role="alert" class="ui-combo__error">{{ error }}</span>
    <span v-if="hint" :id="hintId" class="ui-combo__hint">{{ hint }}</span>
  </div>
</template>

<style scoped>
.ui-combo {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.ui-combo__label {
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
  color: var(--text-label);
}
.ui-combo__label--muted {
  color: var(--text-secondary);
}
.ui-combo__field {
  position: relative;
}
.ui-combo__control {
  box-sizing: border-box;
  width: 100%;
  height: var(--field-height);
  padding: 0 30px 0 10px;
  border: 1px solid var(--opaque-separator);
  border-radius: var(--radius-field);
  font: inherit;
  font-size: var(--type-body-size);
  color: var(--text-primary);
  background: var(--surface-card)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239E9E9E' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")
    no-repeat right 10px center;
  text-overflow: ellipsis;
}
.ui-combo__control:focus {
  outline: none;
  border-color: var(--accent);
}
.ui-combo__control:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.ui-combo__control--compact {
  height: var(--field-height-compact);
}
.ui-combo__control--invalid {
  border-color: var(--error);
}
.ui-combo__list {
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 280px;
  margin: 0;
  padding: 4px;
  overflow-y: auto;
  list-style: none;
  border: 1px solid var(--opaque-separator);
  border-radius: var(--radius-field);
  background: var(--surface-card);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.ui-combo__option {
  padding: 7px 10px;
  border-radius: 6px;
  font-size: var(--type-body-size);
  color: var(--text-primary);
  cursor: pointer;
  overflow-wrap: anywhere;
}
.ui-combo__option--active {
  background: var(--surface-hover);
}
.ui-combo__option--selected {
  font-weight: var(--weight-medium);
}
.ui-combo__note {
  padding: 7px 10px;
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
.ui-combo__error {
  font-size: var(--type-footnote-size);
  color: var(--error);
}
.ui-combo__hint {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
</style>
